import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import CustomButton from '@/components/CustomButton'
import { Toast } from 'react-vant'
import Back from '@/components/Back'
import { APIWeb } from '@/api'
import { toFixedNoRound } from '@/utils'
import { usePartnerLevelStatus } from '@/hooks/listStatus'
import RefreshList from '@/components/RefreshList'
import {copyToClipboard} from '@/utils'
import Big from 'big.js'
export default React.memo(function Promotion() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { getPartnerLevelStatus } = usePartnerLevelStatus()
    const [promotionData, setPromotionData] = useState<GetPromotionCenterResponse | null>(null)

    // 推广列表相关状态
    const [listData, setListData] = useState<ResponsesDirectInviteMember[]>([])
    const [listLoading, setListLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [isClaiming, setIsClaiming] = useState(false)
    const pageSize = 20

    // 获取推广中心数据
    useEffect(() => {
        fetchPromotionData()
    }, [t])
    const fetchPromotionData = async () => {
        Toast.loading({ message: t('加载中'), forbidClick: true })
        try {
            const response = await APIWeb.getPromotionCenter()
            if (response) {
                setPromotionData(response)
            }
        } catch (error) {
            console.error('获取推广中心数据失败:', error)
        } finally {
            Toast.clear()
        }
    }
    // 复制邀请码
    const handleCopyInviteCode = async () => {
        copyToClipboard(promotionData?.invite_code || '')
    }

    // 领取奖励
    const handleClaim = async () => {
        try {
            const amount = toFixedNoRound(promotionData?.w_usd || 0, 2)

            if (Number(amount) <= 0) {
                Toast.info(t('暂无可领取金额'))
                return
            }

            if (isClaiming) return
            setIsClaiming(true)

            Toast.loading({ message: t('处理中'), forbidClick: true })

            const result = await APIWeb.getPromotionIncome({
                amount: amount
            })

            Toast.clear()

            if (result) {
                Toast.success(t('领取成功'))
                // 重新获取推广中心数据以更新余额
                const response = await APIWeb.getPromotionCenter()
                if (response) {
                    setPromotionData(response)
                    fetchPromotionData()
                }
            }
        } catch (error) {
            Toast.clear()
            console.error('领取奖励失败:', error)
        } finally {
            setIsClaiming(false)
        }
    }

    // 获取推广列表数据
    const fetchPromotionList = useCallback(async (page: number, isAppend: boolean) => {
        if (listLoading) return

        setListLoading(true)
        try {
            const response = await APIWeb.getPromotionList({
                page,
                page_size: pageSize
            })

            if (response?.list) {
                const newData = response.list

                if (isAppend) {
                    // 追加数据（用于加载更多）
                    setListData(prevData => [...prevData, ...newData])
                } else {
                    // 替换数据（用于初始加载）
                    setListData(newData)
                }

                // 检查是否还有更多数据
                if (newData.length < pageSize) {
                    setHasMore(false)
                } else {
                    setHasMore(true)
                    if (!isAppend) {
                        setCurrentPage(2) // 初始加载后，下次从第2页开始
                    } else {
                        setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
                    }
                }
            } else {
                setHasMore(false)
            }
        } catch (error) {
            console.error('获取推广列表失败:', error)
            setHasMore(false)
        } finally {
            setListLoading(false)
        }
    }, [listLoading, pageSize])

    // 加载更多数据
    const loadMore = useCallback(async () => {
        await fetchPromotionList(currentPage, true)
    }, [fetchPromotionList, currentPage])

    // 下拉刷新处理函数
    const onRefresh = useCallback(async () => {
        await fetchPromotionList(1, false)
    }, [fetchPromotionList])

    // 渲染推广列表项
    const renderPromotionItem = useCallback((item: ResponsesDirectInviteMember) => {
        return (
            <div className="bg-white px-5 pb-3 mb-3 w-full border-b border-[#F5F5F5] last:border-b-0">
                <div className="text-3.5 text-black/60 mb-2 ml-1">{item.email || '-'}</div>
                <div className='flex items-center gap-2'>
                    <div className="flex items-center gap-2 bg-black/4 rounded-full px-3 py-2">
                        <img className='w-3' src="/icon/virtual_level-icon.svg" alt="level" />
                        <span className="text-3 text-[#666666]">{getPartnerLevelStatus(item.display_level || 0)}</span>
                    </div>
                    <span className="text-3 text-[#000000]">{t('团队')}: {item.team_member_count || 0}</span>
                </div>
            </div>
        )
    }, [getPartnerLevelStatus, t])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className="text-3.75 font-medium text-black">{t('推广中心')}</span>}
            />

            <div className="px-6 py-6">
                {/* 当前等级卡片 */}
                <div className="bg-white rounded-4 p-5 mb-3 border-0.5 border-solid border-[#E8E8E8]">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                            <div className="text-3 text-[#999999] mb-2">{t('当前等级')}</div>
                            <div className="flex items-center gap-2">
                                <div className="text-5 font-500 text-black mb-1">{getPartnerLevelStatus(promotionData?.display_level || 0)}</div>
                                <div className="text-3.5 text-[#666666]">{t('分成X', { percent: Big(promotionData?.level_rate || 0).times(100).toNumber() })}</div>
                            </div>
                        </div>
                        <div className="w-14 h-14 flex items-center justify-center">
                            <img src="/icon/virtual_level-icon.svg" />
                        </div>
                    </div>

                    {/* 进度条 */}
                    <div className="mt-4">
                        <div className="h-2.5 bg-[#D9D9D9] rounded-full overflow-hidden mb-4">
                            <div className="h-full bg-[var(--primary-color)] rounded-full" style={{ width: `${promotionData?.level_rate || 0 * 100}%` }}></div>
                        </div>
                        <div className="text-2.5 text-black text-center">
                            {t('下一等级')}: {getPartnerLevelStatus(promotionData?.display_level || 0 + 1)} | {t('分成X', { percent: Big(promotionData?.next_level_rate || 0).times(100).toNumber() })}
                        </div>
                    </div>
                </div>

                {/* 余额卡片 */}
                <div className="bg-white rounded-4 p-5 mb-3 border-0.5 border-solid border-[#E8E8E8]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
                                <img className='w-full h-full' src="/icon/wusd.svg" alt="usd" />
                            </div>
                            <span className="text-4 font-500 text-black">WUSD</span>
                        </div>
                        <div className="flex-1 text-center text-5.5 font-500 text-black">{toFixedNoRound(promotionData?.w_usd || 0, 2)}</div>
                        <div>
                            <CustomButton
                                onClick={handleClaim}
                                size="small"
                                disabled={isClaiming}
                            >
                                <span className="text-3 ">{t('领取')}</span>
                            </CustomButton>
                        </div>
                    </div>
                </div>

                {/* 邀请码卡片 */}
                <div className="bg-white rounded-4 p-5 mb-3 border-0.5 border-solid border-[#E8E8E8]">
                    <div className="flex justify-between items-center">
                        <span className="text-3.5 text-[#666666]">{t('邀请码')}</span>
                        <span className="text-3.5 font-500 text-black tracking-wide">{promotionData?.invite_code || '-'}</span>
                        <div>
                            <CustomButton
                                onClick={handleCopyInviteCode}
                                size="small"
                                color='default'
                            >
                                <span className="text-3 ">{t('复制')}</span>
                            </CustomButton>
                        </div>
                    </div>
                </div>

                {/* 收益统计网格 */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-white rounded-4 p-5 border-0.5 border-solid border-[#E8E8E8]">
                        <div className="text-3.5 text-black/50 mb-2 font-500">{t('直推奖励')}</div>
                        <div className="text-5 font-500 text-black">{toFixedNoRound(promotionData?.direct_reward_w_usd || 0, 2)} WUSD</div>
                    </div>
                    <div className="bg-white rounded-4 p-5 border-0.5 border-solid border-[#E8E8E8]">
                        <div className="text-3.5 text-black/50 mb-2 font-500">{t('间推奖励')}</div>
                        <div className="text-5 font-500 text-black">{toFixedNoRound(promotionData?.indirect_reward_w_usd || 0, 2)} WUSD</div>
                    </div>
                </div>

                <div className="bg-white rounded-4 p-5 border-0.5 border-solid border-[#E8E8E8] mb-3">
                    <div className="text-3.5 text-black/50 mb-2 font-500">{t('社区收益')}</div>
                    <div className="text-5 font-500 text-black">{toFixedNoRound(promotionData?.community_reward_w_usd || 0, 2)} WUSD</div>
                </div>
                <div className="bg-white rounded-4 p-5 border-0.5 border-solid border-[#E8E8E8] mb-5">
                    <div className="text-3.5 text-black/50 mb-2">{t('团队人数')}</div>
                    <div className="text-5 font-500 text-black">{promotionData?.total_team_member_count || 0}</div>
                </div>

                {/* 我的推广标题 */}
                <div className="text-4 font-600 text-black mb-3 px-1">{t('我的推广')}</div>

                {/* 我的推广列表 */}
                <div className="bg-white rounded-4 border-0.5 border-solid border-[#E8E8E8] overflow-hidden py-6">
                    <div className="max-h-120 min-h-50 overflow-y-auto" style={{ touchAction: 'pan-y' }}>
                        <RefreshList
                            data={listData}
                            renderItem={renderPromotionItem}
                            keyExtractor={(item, index) => `${item.email}-${index}`}
                            loading={listLoading}
                            hasMore={hasMore}
                            onRefresh={onRefresh}
                            onLoadMore={loadMore}
                            emptyText={t('暂无推广记录')}
                            finishedText={t('没有更多数据了')}
                            loadingText={t('加载中')}
                        />
                    </div>
                </div>
            </div>
        </PageLayout>
    )
})
